import dayjs from 'dayjs';

import type {
  ModelExpenseOfDetail,
  ModelExpenseOfDetailsReport,
} from '@app/api/Analytics';
import type { ColumnDataType } from '@app/pages/Analytics/DetailsOfExpensesReport/types';
import { divideAmount } from '@app/utils';

const generateGroupedColumns = (accounts: string[]) => {
  return [
    {
      title: 'Дата',
      dataIndex: 'date',
      fixed: 'left' as const,
      width: 120,
    },
    ...accounts.map((acc) => ({
      title: acc,
      children: [
        {
          title: (
            <div>
              Сумма <br />
              за день
            </div>
          ),
          dataIndex: `${acc}_amount`,
          onCell: () => ({
            className: 'amount-cell',
          }),
        },
        {
          title: 'Покупки (комментарии)',
          dataIndex: `${acc}_comment`,
          width: 250,
        },
      ],
    })),
    {
      title: 'Итого',
      dataIndex: 'total',
      width: 150,
    },
  ];
};

export const transformDetailsOfExpensesReportToTableData = (
  data: ModelExpenseOfDetailsReport | undefined
): {
  dataSource: ColumnDataType[];
  columns: ReturnType<typeof generateGroupedColumns>;
} => {
  if (!data) {
    return { dataSource: [], columns: [] };
  }

  const accountNamesMap = data.AccountWithNames ?? {};
  const days = data.Days ?? [];
  const allAccounts = Object.keys(accountNamesMap);
  const allAccountLabels = allAccounts.map(
    (id: string) => accountNamesMap[id as keyof typeof accountNamesMap]
  ) as string[];

  const dataSource: ColumnDataType[] = days.map((day, index) => {
    const rowData: ColumnDataType = {
      key: `${index}`,
      date: dayjs(day.Date.slice(0, 10)).format('DD.MM.YYYY'),
      total: '',
    };

    const grouped: Record<string, ModelExpenseOfDetail[]> = {};
    for (const acc of allAccounts) grouped[acc] = [];

    let dailyTotal = 0;

    day.Expenses?.forEach((exp) => {
      grouped[exp.AccountId.toString()].push(exp);
      dailyTotal += exp.Amount;
    });

    rowData.total = divideAmount(dailyTotal);

    allAccounts.forEach((accId) => {
      const expenses = grouped[accId];
      const label = accountNamesMap[accId as keyof typeof accountNamesMap];
      const totalAmount = expenses.reduce((sum, e) => sum + e.Amount, 0);
      const comments = expenses.map((e) => e.Comment).join('; ');
      if (totalAmount > 0) {
        rowData[`${label}_amount`] = divideAmount(totalAmount);
        rowData[`${label}_comment`] = comments;
      }
    });

    return rowData;
  });

  const totalsRow: ColumnDataType = {
    key: 'total',
    date: 'Итого',
    total: divideAmount(
      days.reduce(
        (sum, d) => sum + (d.Expenses ?? []).reduce((s, e) => s + e.Amount, 0),
        0
      )
    ),
  };

  allAccounts.forEach((accId) => {
    const label = accountNamesMap[accId as keyof typeof accountNamesMap];
    let amount = 0;

    days.forEach((d) => {
      d.Expenses?.forEach((e) => {
        if (e.AccountId.toString() === accId) {
          amount += e.Amount;
        }
      });
    });

    if (amount > 0) {
      totalsRow[`${label}_amount`] = divideAmount(amount);
    }
  });

  dataSource.push(totalsRow);

  const columns = generateGroupedColumns(allAccountLabels);

  return { dataSource, columns };
};
